import React from 'react';
import { BarChart, Trash2, Layers } from 'lucide-react';
import { formatCurrency } from '../../../../../utils/formatting';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент боковой панели для сравнения сохраненных расчетов виз
 */
const ComparisonSidebar = ({ 
  savedCalculations, 
  deleteSavedCalculation, 
  loadSavedCalculation,
  countries,
  visaTypes
}) => {
  // Если нет сохраненных расчетов
  if (!savedCalculations || savedCalculations.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
          <BarChart className="w-5 h-5 mr-2 text-purple-500" />
          Сравнение расчетов
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Здесь будут отображаться сохраненные расчеты визовых сборов для сравнения.
        </p>
        <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
          <p className="text-sm text-gray-600 dark:text-gray-400 text-center">
            Нажмите "Сохранить расчет" после выполнения расчета для добавления его в список сравнения.
          </p>
        </div>
      </div>
    );
  }

  // Получение информации о стране и типе визы
  const getCountryName = (countryId) => {
    const country = countries.find(c => c.id === countryId);
    return country ? country.name : countryId;
  };

  const getVisaTypeName = (visaTypeId) => {
    const visaType = visaTypes.find(t => t.id === visaTypeId);
    return visaType ? visaType.name : visaTypeId;
  };

  // Форматирование даты сохранения
  const formatSavedDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ru-RU', { 
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <BarChart className="w-5 h-5 mr-2 text-purple-500" />
          Сравнение расчетов
        </h3>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {savedCalculations.length} {savedCalculations.length === 1 ? 'расчет' : savedCalculations.length < 5 ? 'расчета' : 'расчетов'}
        </span>
      </div>

      <div className="space-y-4">
        {savedCalculations.map((calculation) => (
          <div 
            key={calculation.id} 
            className="bg-gray-50 dark:bg-gray-700/30 border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group"
          >
            <div className="flex justify-between items-start mb-2">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                {calculation.name}
              </h4>
              <div className="flex items-center space-x-2">
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Layers className="w-4 h-4" />}
                  onClick={() => loadSavedCalculation(calculation.id)}
                  title="Загрузить параметры"
                />
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Trash2 className="w-4 h-4 text-red-500" />}
                  onClick={() => deleteSavedCalculation(calculation.id)}
                  title="Удалить"
                />
              </div>
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              <div className="flex justify-between mb-1">
                <span>Страна:</span>
                <span className="font-medium">
                  {getCountryName(calculation.country)}
                </span>
              </div>
              <div className="flex justify-between mb-1">
                <span>Тип визы:</span>
                <span>
                  {getVisaTypeName(calculation.visaType)}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Стоимость:</span>
                <span className="font-semibold text-purple-600 dark:text-purple-400">
                  {calculation.results && formatCurrency(calculation.results.totalRubCost)} ₽
                </span>
              </div>
              <div className="mt-2 text-xs text-gray-500 dark:text-gray-500">
                Сохранено: {formatSavedDate(calculation.date)}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Сравнительный анализ - показываем, только если есть хотя бы 2 расчета */}
      {savedCalculations.length >= 2 && (
        <div className="mt-6">
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            Сравнительный анализ
          </h4>
          
          {/* Поиск самого выгодного варианта */}
          {(() => {
            const cheapestCalculation = [...savedCalculations].sort((a, b) => 
              (a.results?.totalRubCost || 0) - (b.results?.totalRubCost || 0)
            )[0];
            
            const mostExpensiveCalculation = [...savedCalculations].sort((a, b) => 
              (b.results?.totalRubCost || 0) - (a.results?.totalRubCost || 0)
            )[0];
            
            const difference = mostExpensiveCalculation.results?.totalRubCost - cheapestCalculation.results?.totalRubCost;
            
            return (
              <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3 border border-green-100 dark:border-green-800 mb-3">
                <p className="text-sm text-green-700 dark:text-green-300">
                  <span className="font-medium">Самый выгодный вариант:</span> {cheapestCalculation.name} 
                  ({formatCurrency(cheapestCalculation.results?.totalRubCost)} ₽)
                </p>
                <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                  Экономия до {formatCurrency(difference)} ₽ по сравнению с самым дорогим вариантом.
                </p>
              </div>
            );
          })()}
          
          {/* Таблица сравнения всех вариантов */}
          <div className="overflow-x-auto mt-3 rounded-lg border border-gray-200 dark:border-gray-700">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-xs">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="px-2 py-2 text-left font-medium text-gray-500 dark:text-gray-400">Виза</th>
                  <th className="px-2 py-2 text-right font-medium text-gray-500 dark:text-gray-400">Консульский сбор</th>
                  <th className="px-2 py-2 text-right font-medium text-gray-500 dark:text-gray-400">Доп. услуги</th>
                  <th className="px-2 py-2 text-right font-medium text-gray-500 dark:text-gray-400">Итого</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {savedCalculations.map(calculation => (
                  <tr key={calculation.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-2 py-2 whitespace-nowrap">{getCountryName(calculation.country)}</td>
                    <td className="px-2 py-2 whitespace-nowrap text-right">
                      {calculation.results?.totalConsulateFee 
                        ? `${formatCurrency(calculation.results.totalConsulateFee * calculation.results.exchangeRates.EUR)} ₽` 
                        : '-'}
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap text-right">
                      {calculation.results?.totalRubAdditionalCosts 
                        ? `${formatCurrency(calculation.results.totalRubAdditionalCosts)} ₽` 
                        : '-'}
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap text-right font-medium text-purple-600 dark:text-purple-400">
                      {calculation.results?.totalRubCost 
                        ? `${formatCurrency(calculation.results.totalRubCost)} ₽` 
                        : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default ComparisonSidebar;